import React from 'react';
import {
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Switch,
  Typography,
} from '@material-ui/core';
import DatePicker from 'components/DatePicker';
import ExplanationHover from 'components/ExplanationHover';
import { isFuture, isValid, parseISO } from 'date-fns';
import { Formik } from 'formik';
import { PartnerFormFields } from 'modules/partners/interfaces';
import moment from 'moment';
import i18n from 'i18n';
import {
  formatDateForEnvStatus,
  formatDateForEnvStatusUpdate,
} from 'utils/helpers';
import clsx from 'clsx';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { modalOpen, modalClose } from 'modules/modal/actions';
import Button from 'components/Button';
import { ModalFields } from 'modules/modal/interfaces';
import styles from './styles';

const useStyles = makeStyles(styles);

interface UseNewFrontConfigurationProps {
  isEdit?: boolean;
  values: PartnerFormFields;
  partnerDetails: PartnerFormFields | null;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  setFieldValue: (field: string, value: any) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

const UseNewFrontConfiguration = ({
  isEdit,
  values,
  partnerDetails,
  modalClose,
  modalOpen,
  setFieldValue,
  setHasError,
}: UseNewFrontConfigurationProps) => {
  const classes = useStyles();

  const updateNewFrontConfiguration = (key, value) => {
    if (key === 'frontend.migration.enabled' && !!value) {
      modalOpen({
        title: i18n.t('PARTNER_PAGE.ENABLE_NEW_FRONT_MIGRATION_KEY'),
        content: i18n.t(
          'PARTNER_PAGE.ENABLE_NEW_FRONT_MIGRATION_CONFIRMATION_KEY'
        ),
        footer: (
          <Grid>
            <Button
              submitButton
              id="submit-button"
              onClick={() => {
                setFieldValue(key, value);
                modalClose();
              }}
            >
              {i18n.t('CONFIRM_KEY')}
            </Button>
            <Button id="cancel-button" variant="outlined" onClick={modalClose}>
              {i18n.t('CANCEL_KEY')}
            </Button>
          </Grid>
        ),
      });
      return;
    }
    const field = key.endsWith('deadline')
      ? formatDateForEnvStatusUpdate(value)
      : value;
    setFieldValue(key, field);
  };

  const isDeadlineValid = deadline => {
    if (!isEdit || !values?.frontend?.migration?.enabled) {
      return true;
    }

    if (values?.frontend?.migration?.enabled && !deadline) {
      return false;
    }

    if (
      partnerDetails?.frontend?.migration?.enabled ===
        values?.frontend?.migration?.enabled &&
      deadline === partnerDetails?.frontend?.migration?.deadline
    ) {
      return true;
    }

    return moment(deadline).isValid() && isFuture(parseISO(deadline));
  };

  React.useEffect(() => {
    setHasError(!isDeadlineValid(values?.frontend?.migration?.deadline));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const renderSwitch = (id, labelKey, value, updateKey, disabledKey) => (
    <FormControlLabel
      classes={{ label: classes.switchLabel }}
      label={
        <Typography className={classes.useNewFront}>
          {i18n.t(labelKey)}
        </Typography>
      }
      control={
        <Switch
          id={id}
          classes={{ colorSecondary: classes.switchChecked }}
          size="small"
          checked={!!value}
          onChange={(_, checked) =>
            updateNewFrontConfiguration(updateKey, !!checked)
          }
          disabled={!!disabledKey}
        />
      }
    />
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Formik initialValues={values} onSubmit={() => {}} enableReinitialize>
      {({ values }) => (
        <Paper elevation={2} className={classes.root}>
          <DialogTitle className={classes.header}>
            {i18n.t('PARTNER_PAGE.NEW_FRONT_CONFIGURATION_KEY')}
          </DialogTitle>

          <DialogContent
            dividers
            className={clsx(classes.content, {
              [classes.contentInCreation]: !isEdit,
            })}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid
                  container
                  alignItems="center"
                  className={classes.fieldset}
                >
                  <Grid item xs={11}>
                    <fieldset className={classes.fieldSetSwitchComponents}>
                      {renderSwitch(
                        'toggle-new-front',
                        'PARTNER_PAGE.ENABLE_NEW_FRONT_KEY',
                        values?.frontend?.enable_new_interface,
                        'frontend.enable_new_interface',
                        values?.frontend?.migration?.enabled
                      )}
                    </fieldset>
                  </Grid>
                  <ExplanationHover
                    title={i18n.t(
                      'PARTNER_PAGE.ENABLE_NEW_FRONT_EXPLANATION_KEY'
                    )}
                  />
                </Grid>
              </Grid>
              {isEdit && (
                <>
                  <Grid item xs={6}>
                    <Grid
                      container
                      alignItems="center"
                      className={classes.fieldset}
                    >
                      <Grid item xs={10}>
                        <fieldset className={classes.fieldSetSwitchComponents}>
                          {renderSwitch(
                            'toggle-new-front-migration',
                            'PARTNER_PAGE.ENABLE_NEW_FRONT_MIGRATION_KEY',
                            values?.frontend?.migration?.enabled,
                            'frontend.migration.enabled',
                            values?.frontend?.enable_new_interface
                          )}
                        </fieldset>
                      </Grid>
                      <ExplanationHover
                        title={i18n.t(
                          'PARTNER_PAGE.ENABLE_NEW_FRONT_MIGRATION_EXPLANATION_KEY'
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid
                      container
                      alignItems="center"
                      className={classes.fieldset}
                    >
                      <Grid item xs={10}>
                        <DatePicker
                          id="new-front-migration-deadline"
                          type="date"
                          label={i18n.t(
                            'PARTNER_PAGE.NEW_FRONT_MIGRATION_DEADLINE_KEY'
                          )}
                          keyValue="frontend.migration.deadline"
                          value={formatDateForEnvStatus(
                            values?.frontend?.migration?.deadline
                          )}
                          setFieldsValue={(key, value) => {
                            if (isValid(value))
                              updateNewFrontConfiguration(key, value);
                          }}
                          minDate={new Date()}
                          disabled={!values?.frontend?.migration?.enabled}
                          error={
                            !isDeadlineValid(
                              values?.frontend?.migration?.deadline
                            )
                          }
                          blockTypeDate
                        />
                      </Grid>
                      <ExplanationHover
                        title={i18n.t(
                          'PARTNER_PAGE.NEW_FRONT_MIGRATION_DEADLINE_EXPLANATION_KEY'
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
        </Paper>
      )}
    </Formik>
  );
};

const mapStateToProps = ({
  partners: { partnerDetails },
}: ApplicationState) => ({
  partnerDetails,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
})(UseNewFrontConfiguration);
